'use client'

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { OPEN_PREFS_EVENT } from '@/lib/consent'
import { currentGroupState, gpcActive, needsPrompt, savePrivacyChoice } from '@/lib/privacyRuntime'

// Non-modal cookie consent banner + the footer's "Your Privacy Choices" panel.
// GDPR-compliant: Accept and Decline carry equal visual weight (both
// cta-secondary — no emerald-Accept dark pattern; emerald stays reserved for
// the toggle's selected state). Never blocks the page; sits below the cart
// drawer (drawer is z-40/50, banner is z-30).
//
// Mounted on every tenant. The first-visit prompt appears only when
// `needsPrompt()` says so — consent mode (banner flag on), an unanswered or
// out-of-date choice, and NEVER for a visitor sending GPC. In opt-out mode it
// renders nothing until the footer link opens the panel. A GPC visitor can
// still opt in from the panel; the panel says their signal is on, and saving
// records the choice as made despite it (privacyDecision.ts, gpcOverride).
//
// The decision reads a cookie the server never renders, so we render null
// until `mounted` to avoid a hydration mismatch / banner flash.

export function CookieConsentBanner({
  privacyHref,
}: {
  /** The store's privacy page. Every store has one (resolvePrivacyPage), so the link always renders. */
  privacyHref: string
}) {
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const [showPrefs, setShowPrefs] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const [gpc, setGpc] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMounted(true)
    setOpen(needsPrompt())
    // Footer "Your Privacy Choices" opens the panel, seeded with what is
    // actually in effect (an opt-out-mode visitor with no choice sees both on).
    const onOpenPrefs = () => {
      const cur = currentGroupState()
      setAnalytics(cur.site_analytics)
      setMarketing(cur.marketing)
      setGpc(gpcActive())
      setShowPrefs(true)
      setOpen(true)
    }
    window.addEventListener(OPEN_PREFS_EVENT, onOpenPrefs)
    return () => window.removeEventListener(OPEN_PREFS_EVENT, onOpenPrefs)
  }, [])

  // Move focus into the panel when it expands (a11y), without trapping it.
  useEffect(() => {
    if (open && showPrefs) panelRef.current?.focus()
  }, [open, showPrefs])

  function commit(choice: { analytics: boolean; marketing: boolean }) {
    savePrivacyChoice({ site_analytics: choice.analytics, marketing: choice.marketing }) // dispatches sifly:consent-updated
    setOpen(false)
    setShowPrefs(false)
  }

  // Closing the panel never counts as a decision (GDPR: silence ≠ consent). It
  // returns to whatever is still owed: the first-visit bar when a consent-mode
  // visitor hasn't answered, otherwise nothing (opt-out mode, or answered).
  function closePanel() {
    setShowPrefs(false)
    setOpen(needsPrompt())
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && showPrefs) {
      e.stopPropagation()
      closePanel()
    }
  }

  if (!mounted || !open) return null

  return (
    // Region-level Escape handler collapses the prefs panel (GDPR: Escape never
    // counts as consent). Intentional keyboard handler on a landmark region.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="region"
      aria-label="Cookie consent"
      aria-live="polite"
      onKeyDown={onKeyDown}
      className="fixed inset-x-0 bottom-0 z-30 border-t bg-[var(--color-background)] text-[var(--color-text)]"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="mx-auto max-w-[var(--max-width-page)] px-4 py-4 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
        {!showPrefs ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="leading-snug">
              We use cookies to improve your experience. See our{' '}
              <a href={privacyHref} className="underline">Privacy Policy</a>.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="cta-secondary rounded-md px-4 py-2 text-sm" onClick={() => setShowPrefs(true)}>
                Manage preferences
              </button>
              <button type="button" className="cta-secondary rounded-md px-4 py-2 text-sm" onClick={() => commit({ analytics: false, marketing: false })}>
                Decline
              </button>
              <button type="button" className="cta-secondary rounded-md px-4 py-2 text-sm" onClick={() => commit({ analytics: true, marketing: true })}>
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div ref={panelRef} tabIndex={-1} className="flex flex-col gap-3 outline-none">
            <div className="flex items-start justify-between gap-3">
              <span className="font-medium">Your Privacy Choices</span>
              <div className="flex items-center gap-3">
                <a href={privacyHref} className="text-xs underline opacity-80">Privacy Policy</a>
                <button type="button" aria-label="Close" className="opacity-70 hover:opacity-100" onClick={closePanel}>
                  <X className="size-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            {gpc && (
              <p className="text-xs leading-snug opacity-80">
                Your browser is sending a Global Privacy Control signal, so we don&apos;t measure your visit or use
                marketing cookies. Turning these on overrides that signal for this site only.
              </p>
            )}
            <ConsentRow label="Necessary" desc="Required for the site to work. Always active." checked disabled />
            <ConsentRow label="Analytics" desc="Helps us measure site traffic." checked={analytics} onChange={setAnalytics} />
            <ConsentRow label="Marketing" desc="Ads, ad measurement and session recording." checked={marketing} onChange={setMarketing} />
            <div className="flex justify-end">
              <button type="button" className="cta-secondary rounded-md px-4 py-2 text-sm" onClick={() => commit({ analytics, marketing })}>
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ConsentRow({
  label, desc, checked, disabled, onChange,
}: {
  label: string; desc: string; checked: boolean; disabled?: boolean; onChange?: (v: boolean) => void
}) {
  return (
    <label className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-70' : 'cursor-pointer'}`}>
      <span>
        <span className="font-medium">{label}</span>
        <span className="block text-xs opacity-80">{desc}</span>
      </span>
      <input
        type="checkbox"
        className="mt-1 size-4 accent-emerald-600"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        aria-label={label}
      />
    </label>
  )
}
